<template>
  <div>
    <NuxtLayout>
      <template #body_page>
        <TripLayout v-if="!shouldRouteChange" :loadingStatus="loadingStatus" :loadingButton='loadingButton' :agentContactApiLoaded="agentContactApiLoaded" />
      </template>
    </NuxtLayout>
  </div>
</template>

<script setup>
import galleryApi from '~/composables/api/galleryApi'
import localDesignerApi from '~/composables/api/localDesignerApi'
import tripApi from '~/composables/api/tripApi'
import enquiryApi from '~/composables/api/enquiryApi'
import accountApi from '~/composables/api/accountApi'
import { DEFINE_META_TAG, TIME_NOTIFY_BOOKABLE } from '~/constant/common'
import { exchangeRate, findExchangeRate, formatCurrencyNoRound, formatCurrencyRound, formatDisplayPrice, getDepositFlat, isEmpty, markupPriceCalculation, publicDomain } from '~/helper'
// import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import TripLayout from './TripLayout.vue'
import { ref, computed, watch, provide, nextTick, onMounted } from "vue"
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useCurrenciesStore } from '~/store/pinia/currencies'
import { useEnquiryCaptureStore } from '~/store/pinia/enquiryCapture'
import { useTripPlanStore } from '~/store/pinia/tripPlan'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

  const currenciesStore = useCurrenciesStore()
  const tripPlanStore = useTripPlanStore()
  const route = useRoute()
  const generalStore = useGeneralInfoStore()
  const { $logger } = useNuxtApp()
  if (route.query?.activity || route.query?.accommodation || route.query?.restaurant || route.query?.transportation) {
    throw createError({
      statusCode: 404,
      fatal: false,
      message: 'Page not found'
    })
  }

  const router = useRouter()
  const enquiryCaptureStore = useEnquiryCaptureStore()
  const dataTrip = ref({})
  const loadingStatus = ref(false)
  const isExpired = ref(false)
  const domain = publicDomain()
  const runtime = useRuntimeConfig()
  const originalPricePerPerson = ref(0)
  const loadingButton = ref(false)
  const agentContactApiLoaded = ref(false)
  const loadedDataStore = ref(false)
  const priceOfTripData = ref({})
  const is_verified = useCookie('is_verified').value
  const is_admin = useCookie('is_admin').value
  let intervalBook = null;
  let iClearLocal = null;
  let key = null; // key check time notify slack


  const LIMIT_LD = 5
  const LIMIT_TRIP = 4
  const LIMIT_BLOG = 10
  const TIME_RUN_CLEAR = 10000;

  const originalPriceEdit = computed(() => {
    return tripPlanStore.originalPriceEdit
  })
  const dataTripStore = computed(() => tripPlanStore.dataTrip)

  const getPriceDisplay = () => {
    let priceDisplay = {}
    const { rate, code } = currencySelected.value

    if (!isEmpty(dataTrip.value) && !isEmpty(originalPriceEdit.value)) {
      if(dataTrip.value.is_booked) {
        if (dataTrip.value.booked_currency !== originalPriceEdit.value.original_currency) {
          const net = exchangeRate(originalPriceEdit.value.original_net_price, 1, dataTrip.value.booked_exchange_rate, true, false, false)
          const deposit = exchangeRate(dataTrip.value.deposit, 1, dataTrip.value.booked_exchange_rate, true, false, false)
          const balance = exchangeRate(dataTrip.value.balance_due, 1, dataTrip.value.booked_exchange_rate, true, false, false)

          priceDisplay = {
            net: formatCurrencyNoRound(parseFloat(net)),
            gross: exchangeRate(originalPriceEdit.value.original_gross_price, 1, dataTrip.value.booked_exchange_rate, true, false),
            // markup: exchangeRate(dataTrip.value.original_markup_price, 1, dataTrip.value.booked_exchange_rate, false, false),
            markup: markupPriceCalculation(originalPriceEdit.value.original_net_price, 1, originalPriceEdit.value.original_adjustment_amount, dataTrip.value.booked_exchange_rate),
            deposit: formatCurrencyNoRound(parseFloat(deposit)),
            balance_due: formatCurrencyNoRound(parseFloat(balance))
          }
        }
        else {
          const markup = originalPriceEdit.value.original_net_price + originalPriceEdit.value.original_adjustment_amount
          priceDisplay = {
            net: formatCurrencyNoRound(originalPriceEdit.value.original_net_price),
            gross: formatCurrencyNoRound(originalPriceEdit.value.original_gross_price),
            // markup: formatCurrencyNoRound(dataTrip.value.original_markup_price),
            markup: formatCurrencyNoRound(markup),
            deposit: formatCurrencyNoRound(dataTrip.value.deposit),
            balance_due: formatCurrencyNoRound(dataTrip.value.balance_due),
          }
        }
      }
      else if (code !== originalPriceEdit.value.original_currency) {
        const currentRate = !isEmpty(dataTrip.value.exchange_rates) ? dataTrip.value.exchange_rates[code] : rate
        const originalRate = !isEmpty(dataTrip.value.exchange_rates) ? 1 : currencyOriginalData.value.rate
        // console.log(code, '=========== currentRate ========== ',currentRate,' =========== ', originalRate, ' ********** ', rate)
        const marketFee = exchangeRate(dataTrip.value.marketplace_fee_base, originalRate, currentRate, true, false, false)
        const net = exchangeRate(originalPriceEdit.value.original_net_price, originalRate, currentRate, true, false, false)
        const depositBase = getDepositFlat(code, net, marketFee)
        const localDeposit = exchangeRate(dataTrip.value.local_deposit, originalRate, currentRate, true, false, false)
        const deposit = depositBase + parseFloat(localDeposit)
        const balance = parseFloat(net) - deposit

        priceDisplay = {
          net: formatCurrencyNoRound(parseFloat(net)),
          gross: exchangeRate(originalPriceEdit.value.original_gross_price, originalRate, currentRate, true, false),
          markup: markupPriceCalculation(originalPriceEdit.value.original_net_price, originalRate, originalPriceEdit.value.original_adjustment_amount, currentRate),
          // markup: exchangeRate(dataTrip.value.original_markup_price, currencyOriginalData.value.rate, rate, false, false),
          deposit: formatCurrencyNoRound(parseFloat(deposit)),
          balance_due: formatCurrencyNoRound(parseFloat(balance)),
        }
      }
      else {
        const depositBase = getDepositFlat(code, originalPriceEdit.value.original_net_price, dataTrip.value.marketplace_fee_base)
        const deposit = depositBase + dataTrip.value.local_deposit
        const balance = originalPriceEdit.value.original_net_price - deposit
        const markup = originalPriceEdit.value.original_net_price + originalPriceEdit.value.original_adjustment_amount

        priceDisplay = {
          net: formatCurrencyNoRound(originalPriceEdit.value.original_net_price),
          gross: formatCurrencyNoRound(originalPriceEdit.value.original_gross_price),
          markup: formatCurrencyNoRound(markup),
          // markup: formatCurrencyNoRound(dataTrip.value.original_markup_price),
          deposit: formatCurrencyNoRound(parseFloat(deposit)),
          balance_due: formatCurrencyNoRound(parseFloat(balance)),
        }
      }
      // store.commit('tripPlanStore/setPriceDisplay', priceDisplay)
      tripPlanStore.setPriceDisplay(priceDisplay)
    }

  }

  const getPriceDisplayGallery = () => {
    let priceDisplay = {}
    const { rate, code } = currencySelected.value

    if (!isEmpty(dataTrip.value) && !isEmpty(originalPriceEdit.value)) {
      if (code !== originalPriceEdit.value.original_currency) {
        const marketFee = exchangeRate(dataTrip.value.marketplace_fee_base, currencyOriginalData.value.rate, rate, false, false, false)
        const net = exchangeRate(originalPriceEdit.value.original_net_price, currencyOriginalData.value.rate, rate, false, false, false)
        const depositBase = getDepositFlat(code, net, marketFee)
        const localDeposit = exchangeRate(dataTrip.value.local_deposit, currencyOriginalData.value.rate, rate, false, false, false)
        const deposit = depositBase + parseFloat(localDeposit)
        const balance = parseFloat(net) - deposit

        priceDisplay = {
          perPerson: exchangeRate(originalPricePerPerson.value, currencyOriginalData.value.rate, rate, false, true, true),
          net: exchangeRate(originalPriceEdit.value.original_net_price, currencyOriginalData.value.rate, rate, false, true, true),
          gross: exchangeRate(originalPriceEdit.value.original_gross_price, currencyOriginalData.value.rate, rate, false, true, true),
          markup: markupPriceCalculation(originalPriceEdit.value.original_net_price, currencyOriginalData.value.rate, dataTrip.value.original_adjustment_amount, rate, true),
          // markup: exchangeRate(dataTrip.value.original_markup_price, currencyOriginalData.value.rate, rate, false, true, true),
          deposit: formatCurrencyRound(parseFloat(deposit)),
          balance_due: formatCurrencyRound(parseFloat(balance))
        }
      } else {
        const depositBase = getDepositFlat(code, originalPriceEdit.value.original_net_price, dataTrip.value.marketplace_fee_base)
        const deposit = depositBase + dataTrip.value.local_deposit
        const balance = originalPriceEdit.value.original_net_price - deposit
        const markup = originalPriceEdit.value.original_net_price + originalPriceEdit.value.original_adjustment_amount

        priceDisplay = {
          perPerson: formatCurrencyRound(originalPricePerPerson.value),
          net: formatCurrencyRound(originalPriceEdit.value.original_net_price),
          gross: formatCurrencyRound(originalPriceEdit.value.original_gross_price),
          markup: formatCurrencyRound(markup),
          deposit: formatCurrencyRound(parseFloat(deposit)),
          balance_due: formatCurrencyRound(parseFloat(balance)),
        }
      }
      tripPlanStore.setPriceDisplayGallery(priceDisplay)
    }
  }

  const getCurrencyDisplay = computed(() => {
    return priceOfTripData.value?.display_currency_symbol || 'AU$'
  })

  const pageType = computed(() => {
    if (route.name === 'trip-plan-bookable') return 'bookable'
    if (route.name === 'trip-preview') return 'preview'
    return 'gallery'
  })
  const isSimplified = computed(() => dataTrip.value.is_simplified !== false)

  const currencySelected = computed(() => {
    // return store.state.currenciesStore.currencySelected
    return currenciesStore.currencySelected
  })
  const currenciesData = computed(() => {
    // return store.state.currenciesStore.currenciesData || []
    return currenciesStore.currenciesData || []
  })

  const currencyOriginalData = computed(() => tripPlanStore.originalRate)

  const typeTripPlan = computed(() => {
    let type = ''

    if(route.name === 'trip-plan' && domain === 'agent' || route.name === 'trip-plan' && domain === 'traveller') type = 'gallery_trip_plan'
    if(route.name === 'trip-plan-bookable' && domain === 'agent' || route.name === 'trip-plan-bookable' && domain === 'traveller') type = 'bookable_trip_plan'
    if(domain === 'preview') type = 'preview_trip_plan'
    if(domain === 'agentpreview') type = 'agent_preview_trip_plan'

    return type
  })

  provide('dataTrip', dataTrip)
  provide('pageType', pageType)
  provide('isSimplified', isSimplified)
  provide('isExpired', isExpired)
  provide('currencyDisplay', getCurrencyDisplay)
  provide('typeTripPlan', typeTripPlan)
  provide('pageLoaded', loadingStatus)

  const showRemaining = () => {
    const intervalFunction = () => {
      const now = new Date();
      const end = new Date(dataTrip.value?.valid_time);
      const distance = end.getTime() - now.getTime();
      if (distance <= 0) {
        isExpired.value = true
        if (isCompleted.value || dataTrip.value?.is_booked) {
          isExpired.value = false
        }
        return distance;
      }
      return 1;
    }
    intervalFunction();
    const timer = setInterval(() => {
      const result = intervalFunction();
      if (result <= 0) {
        clearInterval(timer);
      }
    }, 1000)
  }

  const shouldRouteChange =computed(() => tripPlanStore.tripPlanRouteChange)
  const isCompleted = computed(() => tripPlanStore.isCompleted)

  const postTripPlanViewed = async (tripID) => {
    try {
      if(tripID) {
        const res = (await tripApi.postNumOfView(tripID)).data
        if(!res.success) {
          $logger.log(res?.message, {cause: {function: 'Post trip plan viewed error'}})
        }
      }
    } catch (e) {
      console.log(e)
      $logger.log(e, {cause: {function: 'Post trip plan viewed error'}})
    }
  }

  const getPriceOfTripData = async () => {
    try {
      const resp = await tripApi.getPriceOfTrip(route.params.trip, {currency: currencySelected.value.code})
      if(resp?.data?.value?.success && resp?.data?.value?.data) {
        priceOfTripData.value = resp?.data.value?.data
      } else {
        $logger.log(resp?.data?.value?.message, {cause: {function: 'Get price of trip data error'}})
      }
    } catch (e) {
      console.log(e)
      throw new Error('Price trip error: ', { cause: e })
    }
  }

  const getTripData = async () => {
    try {
      return await tripApi.getTrip(route.params.trip)
    } catch (e) {
      console.log(e)
      throw new Error('Get trip data error : ', { cause: e })
    }
  }

  const setPriceOfTripData = () => {
    const round = priceOfTripData.value?.type === 'template'
    const displayPriceAPI = {
      currency_symbol: priceOfTripData.value?.display_currency_symbol || 'AU$',
      currency: priceOfTripData.value?.display_currency || 'AUD',
      adjustment_amount: formatDisplayPrice(priceOfTripData.value?.display_adjustment_amount, round),
      balance_due: formatDisplayPrice(priceOfTripData.value?.display_balance_due, round),
      deposit: formatDisplayPrice(priceOfTripData.value?.display_deposit, round),
      gross: formatDisplayPrice(priceOfTripData.value?.display_gross_price, round),
      local_deposit: formatDisplayPrice(priceOfTripData.value?.display_local_deposit, round),
      marketplace_fee_base: formatDisplayPrice(priceOfTripData.value?.display_marketplace_fee_base, round),
      markup: formatDisplayPrice(priceOfTripData.value?.display_markup_price, round),
      net: formatDisplayPrice(priceOfTripData.value?.display_net_price, round),
      perPerson: formatDisplayPrice(priceOfTripData.value?.display_net_price, round),
      tracking: {
        gross: priceOfTripData.value?.display_gross_price,
        net: priceOfTripData.value?.display_net_price,
        markup: priceOfTripData.value?.display_markup_price,
        perPerson: priceOfTripData.value?.display_net_price,
        wholesale: priceOfTripData.value?.display_wholesale_price ? priceOfTripData.value?.display_wholesale_price : 0,
        converted_gross: priceOfTripData.value?.converted_gross_price,
        converted_net: priceOfTripData.value?.converted_net_price,
        converted_markup: priceOfTripData.value?.converted_markup_price,
        converted_perPerson: priceOfTripData.value?.converted_budget_per_traveller,
        converted_wholesale: priceOfTripData.value?.converted_wholesale_price
      }
    }
    tripPlanStore.setGroupBookingData({zeroGroupTrip: priceOfTripData.value?.display_gross_price === 0 && dataTrip.value?.is_group_enquiry})
    tripPlanStore.setPriceDisplayAPI(displayPriceAPI)
  }

  const init = () => {
    try {
      if ((route.name === 'trip-plan') && dataTrip.value && process.client && (window.IS_BOT !== 1) && dataTrip.value?.id) {
        postTripPlanViewed(dataTrip.value?.id)
      }

      if(shouldRouteChange.value) {
        if(['traveller', 'agent'].indexOf(domain) !== -1 ){
          localStorage.setItem('dataTrip', JSON.stringify(dataTrip.value))
          if(!is_verified) {
            router.push({
              name: 'trip-gallery',
            });
          }
          else window.location.href = router.resolve({name : 'enquiries-listing'}).href;
        }
        else {
          window.location.href = `${useRuntimeConfig().public.publicDomain}/trip-gallery`;
        }
      }

      if (dataTrip.value?.type === 'bookable') {
        if(dataTrip.value?.is_under_review === true) {
          loadingStatus.value = false
          return router.push({ name: 'enquiries-listing' })
        }
        else if (route.name !== 'trip-plan-bookable' && !shouldRouteChange.value)
          return router.push({
            name: 'trip-plan-bookable',
            params: { trip: dataTrip.value?.id },
          })
      }

      triggerNotifyViewBookable()
    } catch (e) {
      console.log('showError: ',e)
      loadingStatus.value = false
      throw createError({
        statusCode: 404,
        fatal: true,
        statusMessage: e.message,
        data: null
      })
    }
  }

  // onBeforeRouteLeave(() => {
  //   if(process.server) return
  //   enquiryCaptureStore.removeRouteStore({path: route.path}) //clear local storage keep session storage
  // })

  const currencyLoaded = computed(() => currenciesStore.loaded /*store.state.currenciesStore.loaded*/)

  const getTripPlanData = async () => {
    let resp = {
      data: null
    }

    if(!isEmpty(dataTripStore.value) && process.client) {
      const slug = dataTripStore.value?.slug
      if(route.params.trip === slug) {
        loadingStatus.value = true
        dataTrip.value = dataTripStore.value
        loadedDataStore.value = true
        return
      }
    }
    const results = await Promise.all([getTripData(), getPriceOfTripData()])
    if(results.length > 0){
      resp = results[0].data.value
    }

    if (resp.data === null) {
      loadingStatus.value = false
      // throw new Error('Trip not found')
      console.log('trip not found', route.path, route.query)
      throw createError({
        statusCode: 404,
        fatal: false,
        message: 'Trip not found'
      })
      return;
    }

    dataTrip.value = resp.data
    // store.commit('tripPlanStore/setDataTrip', dataTrip.value)
    tripPlanStore.setDataTrip(resp.data)

    setPriceOfTripData()

    const originalPrice = {
      original_adjustment_amount: dataTrip.value?.original_adjustment_amount,
      original_currency: dataTrip.value?.original_currency || 'AUD',
      original_gross_price: dataTrip.value?.original_gross_price,
      original_net_price: dataTrip.value?.original_net_price
    }
    tripPlanStore.setOriginalPriceEdit(originalPrice)

    originalPricePerPerson.value = dataTrip.value?.original_net_price / dataTrip.value?.pax

    if(dataTrip.value?.is_booked) currenciesStore.setTripBooked(dataTrip.value.is_booked)

    loadingStatus.value = true

    if(currencyLoaded.value) {
      const currencyOriginal = findExchangeRate(currenciesData.value,dataTrip.value?.original_currency)
      tripPlanStore.setOriginalRate(currencyOriginal)

      getPriceDisplay()
      getPriceDisplayGallery()
    }
    if((domain !== 'agentpreview') && dataTrip.value !== null) {
      if(process.client) showRemaining()
      loadingButton.value = true
    }
  }

  async function getDesignerData(id) {
    try {
      if (id !== 0 && id) {
        const resp =  (await localDesignerApi.getDesigner(id, { source: 'profile-box' })).data.value
        if(resp?.success && resp?.data) {
          dataTrip.value.designer = resp.data
        } else {
          $logger.log(resp?.message, {cause: {function: 'Get designer data error'}})
        }
      }
    } catch (e) {
      console.log(e)
      throw new Error('Get designer data error : ', { cause: e })
    }
  }

  async function getSuggestedTrip() {
    if(dataTrip.value !== null) {
      try {
        if (route.name !== 'trip-plan') {
          dataTrip.value.suggest_trips = []
          return
        }

        const params = {
          limit: LIMIT_TRIP,
          currency: currencySelected.value.code,
          full_card: 1
        }

        const resp = (await tripApi.getSuggestedTrip(route.params.trip, params)).data.value
        if(resp?.success && resp?.data) {
          dataTrip.value.suggest_trips = resp?.data
        } else {
          dataTrip.value.suggest_trips = []
          $logger.log(resp?.message, {cause: {function: 'Get suggested trip error'}})
        }
      } catch (e) {
        dataTrip.value.suggest_trips = []
        console.log(e)
        $logger.log(e, {cause: {function: 'Get suggested trip error'}})
      }
    }
  }
  
  async function getSuggestedLocalDesigner() {
    if(dataTrip.value !== null && dataTrip.value?.country_ids && dataTrip.value?.country_ids?.length) {
      try {
        if (route.name !== 'trip-plan') {
          dataTrip.value.suggest_trips = []
          return
        }
        const params = {
          country_codes: dataTrip.value?.country_ids.join(','),
          limit: LIMIT_LD,
          designer_id: dataTrip.value?.designer?.id || undefined
        }
        const resp = await localDesignerApi.getLocalDesignersAssigned(params)
        if(resp?.data?.success && resp?.data?.data) {
          dataTrip.value.suggest_local_designer = resp.data.data?.items || []
        } else {
          dataTrip.value.suggest_local_designer = []
          $logger.log(resp?.data?.message, {cause: {function: 'Get suggested local designer error'}})
        }
      } catch (e) {
        dataTrip.value.suggest_local_designer = []
        console.log(e)
        $logger.log(e, {cause: {function: 'Get suggested local designer error'}})
      }
    }
  }
  
  async function getBlogsInspiration() {
    try {
      if(dataTrip.value !== null) {
        if (route.name !== 'trip-plan') {
          dataTrip.value.list_blogs = []
          return
        }
        const category = dataTrip.value?.map_note?.countries || []
        if(!category.length) return
        const params = {
          category: category.join(','),
          limit: LIMIT_BLOG,
          sort_by: 'date',
          sort_type: 'desc',
        }
        const resp =  (await galleryApi.getBlogPost(params)).data.value
        if(resp?.success && resp?.data && resp?.data?.length) {
          dataTrip.value.list_blogs = resp.data.map(({title, link}) => ({name: title, link }))
        } else {
          dataTrip.value.list_blogs = []
          $logger.log(resp?.message, {cause: {function: 'Get blogs data error'}})
        }
      }
    } catch (e) {
      console.log(e)
      dataTrip.value.list_blogs = []
      throw new Error('Get blogs data error : ', { cause: e })
    }
  }

  async function getEnquiryDetails() {
    try {
      if(route.name === 'trip-plan-bookable') {
        const respEnquiry = await enquiryApi.enquiryDetail(dataTrip.value?.enquiry_id);
        const dataEnquiry = respEnquiry.data.data
        tripPlanStore.setEnquiryData(dataEnquiry)
      }
    } catch (e) {
      console.log(e)
      throw new Error('Get designer data error : ', { cause: e })
    }
  }

  async function handleFetchingData() {
    try {
      if(isEmpty(dataTrip.value) && loadedDataStore.value) return
      const arr = [getSuggestedTrip(), getSuggestedLocalDesigner(), getBlogsInspiration(), getEnquiryDetails()]
      await Promise.allSettled(arr)
    } catch (error) {
      console.log({error});
      $logger.log(error, {cause: {function: 'Handle fetching data error'}})
    }
  }

  function triggerNotifyViewBookable() {
    const tripId = route.path.split('/').slice(-1).toString()
    if (pageType.value === 'bookable') {
      sessionStorage.setItem('trip_id', tripId)

      /* for tracking engagement */
      if (route.query.traffic && !is_admin) {
        callSendTrackingBookable(tripId)
      }
      const query = Object.assign({}, route.query)
      delete query.traffic
      router.replace({ query })
      if (!intervalBook && (!is_admin || (is_admin && is_admin != 1))) {
        key = tripId // set key for save in local_storage
        const tripObj = JSON.parse(localStorage.getItem(key))
        const s_time = tripObj ? tripObj.time : null
        /* check for slack notify */
        if (!tripObj || new Date().getTime() - s_time >= TIME_NOTIFY_BOOKABLE) {
          callSendNotify(tripId)
          const obj = {
            id: tripId,
            time: new Date().getTime(),
          }
          localStorage.setItem(key, JSON.stringify(obj))
        }

        /* interval run remove localstorage key that expires time more than 5 minutes */
        iClearLocal = setInterval(() => {
          for (let i = 0; i < localStorage.length; i++) {
            if (localStorage.key(i) !== tripId) {
              let localObj = localStorage.getItem(localStorage.key(i))
              if (localObj.substr(0, 1) === '{') {
                localObj = JSON.parse(localObj)
                let now = new Date().getTime()
                if (now - localObj.time > TIME_NOTIFY_BOOKABLE) {
                  localStorage.removeItem(localStorage.key(i))
                }
              }
            }
          }
        }, TIME_RUN_CLEAR)
        document.addEventListener('mousemove', triggerMouseMove)
        document.tripId = tripId
      }
    }
  }

  function callSendTrackingBookable(tripId) {
    const payload = {
      type: 'bookable',
      trip_id: tripId,
    }
    accountApi
      .sendTrackingEngagement(payload)
      .then((res) => {
        if (res.data.status !== 200) {
          $logger.log(res.data.message, {cause: {function: 'Call send tracking bookable error'}})
          console.log(res.data.message)
        }
      })
      .catch((err) => {
        $logger.log(err.toString(), {cause: {function: 'Call send tracking bookable error'}})
        console.log('err', err.toString())
      })
  }

  /* logic sent notify bookable */
  let mousemove = true;
  let sentNoti = false;
  function triggerMouseMove(evt) {
    mousemove = true;
    clearInterval(intervalBook)
    intervalBook = setInterval(fnInterval, TIME_NOTIFY_BOOKABLE)

    if (sentNoti) {
      callSendNotify(evt.currentTarget.tripId)
      sentNoti = false;
    }

    let obj = {
      id: evt.currentTarget.tripId,
      time: new Date().getTime()
    }
    localStorage.setItem(key, JSON.stringify(obj))
    mousemove = false;
  }

  function callSendNotify(trip_id) {
    let params = {
      type: 1,
      trip_id: trip_id,
    }
    accountApi.sendNotify(params).then((res) => {
      if (res.data.status !== 200) {
        console.log(res.data.message);
        $logger.log(res.data.message, {cause: {function: 'Call send notify error'}})
      }
    }).catch(err => {
      $logger.log(err.toString(), {cause: {function: 'Call send notify error'}})
      console.log('err', err.toString())
    })
  }

  function fnInterval() {
    if (!mousemove) {
      sentNoti = true;
    }
  }
  /* end logic sent notify */

  onMounted(() => {
    // store.commit('generalStore/setLoading', false)
    // if(route.name === 'trip-plan') {
    //   debounceAmplitudeScriptTracking("View Gallery Trip Plan")
    // }
    // if(route.name === 'trip-plan-bookable') {
    //   debounceAmplitudeScriptTracking("View Bookable Trip Plan")
    // }
    nextTick(()=> {
      init()
      handleFetchingData()
    })
  })

  // onBeforeRouteLeave(() => {
  //   if(process.server) return
  //   clearInterval(intervalBook)
  //   clearInterval(iClearLocal)
  //   enquiryCaptureStore.removeRouteStore({path: route.path}) //clear local storage keep session storage
  //   document.removeEventListener('mousemove', triggerMouseMove)
  // })

  const changedCurrency = computed(() => currenciesStore.currencySelected /*store.state.currenciesStore.currencySelected*/)

  watch([changedCurrency], async () => {
      if(domain !== 'agentpreview') {
        await getPriceOfTripData()
      }
      setPriceOfTripData()
      getPriceDisplay()
      getPriceDisplayGallery()
    },
    { deep: true }
  )

  const titlePage = computed(() => {
    if(isEmpty(dataTrip.value) || !dataTrip.value.designer) return ""
    if(route.name === 'trip-plan-bookable') {
      return dataTrip.value.name + ' - by ' + dataTrip.value.designer?.name
    } else if(route.name === 'trip-preview') {
      return dataTrip.value.name
    } else {
      return dataTrip.value.meta_title?.length ? dataTrip.value.meta_title : dataTrip.value.name + ' | Designer Journeys'
    }
  })

  const descriptionPage = computed(() => {
    if(isEmpty(dataTrip.value) || !dataTrip.value.designer) return ""
    if(route.name === 'trip-plan-bookable') {
      return dataTrip.value.meta_description?.length ? dataTrip.value.meta_description : dataTrip.value.overview
    } else if(route.name === 'trip-preview') {
      return 'Book ' + dataTrip.value.name + ' or customise it to create the perfect trip experience.'
    } else {
      return dataTrip.value.meta_description?.length ? dataTrip.value.meta_description : dataTrip.value.overview
    }
  })

  const canonical = runtime.public.publicDomain + route.path
  useHead({
    title: computed(() => {
      if(dataTrip.value && dataTrip.value.designer) {
        if (route.name === 'trip-plan-bookable'){
          const title = dataTrip.value?.name + ' - by ' + dataTrip.value?.designer?.name
          return title

        } if(route.name === 'trip-preview' ) {
          const title = dataTrip.value?.name
          return title
        }
        else {
          const title = dataTrip.value?.meta_title?.length ? dataTrip.value?.meta_title : dataTrip.value?.name + ' | Designer Journeys'
          return title
        }
      } else {
        return ''
      }
    }),

    meta: [
      ...DEFINE_META_TAG,
        {
          name: 'robots',
          content: useAppConfig().disableBotsIndexing == 1 ? 'NOINDEX, NOFOLLOW' : 'INDEX, FOLLOW'
        },
        {
          name: `title`,
          content: computed(()=> titlePage.value),
        },
        {
          name: `description`,
          content: computed(()=> descriptionPage.value),
        },
      ],
    link: computed(() => {
      if(dataTrip.value && dataTrip.value.designer) {
        return [
          { rel: 'canonical', href: `${canonical}`, hreflang: 'en'}
        ]
      } else {
        return ''
      }
    }),
    // script: computed(() => {
    //   return domain === 'traveller' ? [{ type: 'text/javascript', src: 'https://www.googleoptimize.com/optimize.js?id=GTM-5NRNSCQ' }] : ''
    // })
  })
  useSeoMeta({
    ogTitle:  computed(()=> titlePage.value),
    ogDescription:  computed(()=> descriptionPage.value),
    ogType: 'website',
    ogUrl: `${runtime.public.publicDomain}${route.fullPath}`,
    ogSiteName: 'Designer Journeys',
    ogImage: computed(()=>dataTrip.value?.meta_image),
    fbAppId: '452745475618385',
    href: `${runtime.public.publicDomain}${route.fullPath}`,
    twitterTitle:  computed(()=> titlePage.value),
    twitterDescription:  computed(()=> descriptionPage.value),
    twitterImageAlt: computed(()=> titlePage.value),
    twitterCard: 'summary',
    twitterImage: computed(()=>dataTrip.value?.meta_image),
  })
  if(process.client) {
    generalStore.setLoading(true)
  }
  await getTripPlanData()
  if(dataTrip.value.designer?.id && !loadedDataStore.value) {
    await getDesignerData(dataTrip.value.designer.id)
  }
  generalStore.setLoading(false)

  if(route.name === 'trip-plan' && ((dataTrip.value && dataTrip.value?.slug) !== route.params.trip)) {
    navigateTo({path: `/trip-gallery/${dataTrip.value?.slug}`}, { redirectCode: 301 })
  }
  onBeforeRouteLeave(() => {
    clearInterval(intervalBook)
    clearInterval(iClearLocal)
    enquiryCaptureStore.removeRouteStore({path: route.path}) //clear local storage keep session storage
    document.removeEventListener('mousemove', triggerMouseMove)
  })
</script>

<style>
.description a {
  color: #000;
  text-decoration: underline !important;
}
</style>

